import type { ColumnDefinition } from '@/components/atomic/table/types'

import * as TableColumns from '@/components/atomic/table/columns'
import { RATING_COLUMN } from '@/components/atomic/table/columns'
import { ProposalDetailsOrdersTableColumnId } from '@/types/enums/proposals'

// All possible columns with default hide=true
const allColumnDefinitions: Map<
  ProposalDetailsOrdersTableColumnId,
  ColumnDefinition
> = new Map([
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_DESCRIPTION,
    TableColumns.PROPOSAL_ORDER_DESCRIPTION.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_ISIN,
    TableColumns.PROPOSAL_ORDER_ISIN.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_MATURITY_DATE,
    TableColumns.PROPOSAL_ORDER_MATURITY_DATE.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SP_RATING,
    RATING_COLUMN({
      hide: true,
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SP_RATING,
      field: 'sp_rating',
    }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SECTOR,
    TableColumns.PROPOSAL_ORDER_SECTOR.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_AMOUNT_AND_QUANTITY,
    TableColumns.PROPOSAL_ORDER_AMOUNT_AND_QUANTITY.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_DURATION,
    TableColumns.PROPOSAL_ORDER_DURATION.create({ hide: true }),
  ],
  // yields
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_LIMIT_YTW,
    TableColumns.PROPOSAL_ORDER_LIMIT_YTW.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_EVAL_YTW,
    TableColumns.PROPOSAL_ORDER_EVAL_YTW.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_EVAL_YTW_DIFFERENCE,
    TableColumns.PROPOSAL_ORDER_EVAL_YTW_DIFFERENCE.create({ hide: true }),
  ],
  // prices
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_LIMIT_PRICE,
    TableColumns.PROPOSAL_ORDER_BOND_SWAP_LIMIT_PRICE.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_EVAL_PRICE,
    TableColumns.PROPOSAL_ORDER_BOND_SWAP_EVAL_PRICE.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_PRICE_DIFFERENCE,
    TableColumns.PROPOSAL_BOND_SWAP_ORDER_PRICE_DIFFERENCE.create({
      hide: true,
    }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SELECT,
    TableColumns.PROPOSAL_ORDER_SELECT.create({ hide: true }),
  ],
])

// Columns to show for bond swap table
const bondSwapColumnDefinitions: Map<
  ProposalDetailsOrdersTableColumnId,
  ColumnDefinition
> = new Map([
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_DESCRIPTION,
    TableColumns.PROPOSAL_ORDER_DESCRIPTION.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_ISIN,
    TableColumns.PROPOSAL_ORDER_ISIN.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_MATURITY_DATE,
    TableColumns.PROPOSAL_ORDER_MATURITY_DATE.create({ hide: true }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SP_RATING,
    RATING_COLUMN({
      hide: false,
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SP_RATING,
      field: 'sp_rating',
    }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SECTOR,
    TableColumns.PROPOSAL_ORDER_SECTOR.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_AMOUNT_AND_QUANTITY,
    TableColumns.PROPOSAL_ORDER_AMOUNT_AND_QUANTITY.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_DURATION,
    TableColumns.PROPOSAL_ORDER_DURATION.create({ hide: false }),
  ],
  // yields
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_LIMIT_YTW,
    TableColumns.PROPOSAL_ORDER_LIMIT_YTW.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_EVAL_YTW,
    TableColumns.PROPOSAL_ORDER_EVAL_YTW.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_EVAL_YTW_DIFFERENCE,
    TableColumns.PROPOSAL_ORDER_EVAL_YTW_DIFFERENCE.create({ hide: false }),
  ],
  // prices
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_LIMIT_PRICE,
    TableColumns.PROPOSAL_ORDER_BOND_SWAP_LIMIT_PRICE.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_EVAL_PRICE,
    TableColumns.PROPOSAL_ORDER_BOND_SWAP_EVAL_PRICE.create({ hide: false }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_PRICE_DIFFERENCE,
    TableColumns.PROPOSAL_BOND_SWAP_ORDER_PRICE_DIFFERENCE.create({
      hide: false,
    }),
  ],
  [
    ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SELECT,
    TableColumns.PROPOSAL_ORDER_SELECT.create({ hide: false }),
  ],
])

export function getColumnDefinitions(): ColumnDefinition[] {
  // Create a new map for our final column definitions
  const finalColumns = new Map<
    ProposalDetailsOrdersTableColumnId,
    ColumnDefinition
  >()

  // Add all columns, setting hide based on whether they're in the visible columns
  allColumnDefinitions.forEach((colDef, colId) => {
    const isVisible = bondSwapColumnDefinitions.has(colId)
    finalColumns.set(colId, {
      ...colDef,
      hide: !isVisible,
    })
  })

  return Array.from(finalColumns.values())
}
